import { eq, and } from 'drizzle-orm'
import { createTransport } from 'nodemailer'
import { inngest } from '@/lib/inngest'
import { db as defaultDb } from '@/db'
import { tickets, ticketMessages, aiArtifacts, tenantConfigs, auditLog, users } from '@/db/schema'
import { transition } from '@/lib/ticket-state-machine'
import { buildCompletenessPrompt } from '../../prompts/completeness-assessment'
import { callCompletenessLLM, type CompletenessResult, type AIGatewayConfig } from '@/lib/ai-gateway'
import { buildClarificationQuestionsEmail } from '@/lib/clarification-questions-email'
import { buildTicketQueuedEmail } from '@/lib/ticket-queued-email'
import type { PostgresJsDatabase } from 'drizzle-orm/postgres-js'
import type * as schema from '@/db/schema'

type DB = PostgresJsDatabase<typeof schema>

export type AssessCompletenessInput = {
  ticketId: string
  tenantId: string
  messageId: string
  ticketVersion: number
}

/** Pure business logic — separated from Inngest wrapper for testability. */
export async function runCompletenessAssessment(
  input: AssessCompletenessInput,
  options?: {
    db?: DB
    callLLM?: (config: AIGatewayConfig, system: string, user: string) => Promise<CompletenessResult>
  },
): Promise<{
  outcome: 'NEEDS_INFO' | 'TRIAGED' | 'AWAITING_EXPERT'
  score?: number
  ticketVersion?: number
}> {
  const db = (options?.db ?? defaultDb) as DB
  const llm = options?.callLLM ?? callCompletenessLLM

  // Load ticket, checking it's still at the version we were triggered on
  const [ticket] = await db
    .select()
    .from(tickets)
    .where(and(eq(tickets.id, input.ticketId), eq(tickets.tenantId, input.tenantId)))
    .limit(1)

  if (!ticket) throw new Error(`Ticket not found: ${input.ticketId}`)

  // Stale-run guard: if the ticket has moved on, bail out
  if (ticket.version !== input.ticketVersion || ticket.status !== 'SUBMITTED') {
    return { outcome: 'AWAITING_EXPERT' }
  }

  // Load tenant config for thresholds and AI settings
  const [config] = await db
    .select()
    .from(tenantConfigs)
    .where(eq(tenantConfigs.tenantId, input.tenantId))
    .limit(1)

  if (!config) throw new Error(`Tenant config not found for tenant: ${input.tenantId}`)

  // Structured-fields gate: if any required intake field is missing, skip LLM and request info
  const structuredRequired = [
    'projectId',
    'department',
    'dateOfOccurrence',
    'issueCategory',
    'affectedSystem',
    'userPriority',
    'successCriteria',
    'actionRequested',
  ] as const
  const missingFields = structuredRequired.filter((f) => !ticket[f])
  if (missingFields.length > 0 && ticket.clarificationCycleCount < config.maxClarificationCycles) {
    const fieldLabels: Record<string, string> = {
      projectId: 'Project',
      department: 'Department',
      dateOfOccurrence: 'Date of Occurrence',
      issueCategory: 'Issue Category',
      affectedSystem: 'Affected System',
      userPriority: 'Priority',
      successCriteria: 'Success Criteria',
      actionRequested: 'Action Requested',
    }
    const missingLabels = missingFields.map((f) => fieldLabels[f] ?? f)

    await db.insert(ticketMessages).values({
      tenantId: input.tenantId,
      ticketId: input.ticketId,
      authorId: null,
      role: 'system',
      bodyRaw: `Please complete the following required fields: ${missingLabels.join(', ')}.`,
      bodySanitized: `Please complete the following required fields: ${missingLabels.join(', ')}.`,
    })

    await db.insert(aiArtifacts).values({
      tenantId: input.tenantId,
      ticketId: input.ticketId,
      sourceMessageId: input.messageId,
      triggerEvent: 'ticket/submitted',
      artifactType: 'clarification_questions',
      content: `Missing required fields: ${missingLabels.join(', ')}.`,
      contentJson: JSON.stringify({ questions: missingLabels.map((l) => `Please provide: ${l}`) }),
      modelUsed: null,
      promptVersion: '1.0.0',
      validationStatus: 'valid',
    })

    await applyTransition(db, ticket, 'NEEDS_INFO', null, null, input, 'missing_structured_fields')
    const missingQuestions = missingLabels.map((l) => `Please provide: ${l}`)
    await sendAssessmentEmail(db, ticket, input.tenantId, 'NEEDS_INFO', missingQuestions)
    return { outcome: 'NEEDS_INFO' }
  }

  // Load the triggering message
  const [triggerMessage] = await db
    .select()
    .from(ticketMessages)
    .where(eq(ticketMessages.id, input.messageId))
    .limit(1)

  if (!triggerMessage) throw new Error(`Message not found: ${input.messageId}`)

  // Load prior clarification Q&A pairs for context
  const priorArtifacts = await db
    .select()
    .from(aiArtifacts)
    .where(
      and(
        eq(aiArtifacts.ticketId, input.ticketId),
        eq(aiArtifacts.artifactType, 'clarification_questions'),
      ),
    )
    .orderBy(aiArtifacts.createdAt)

  const priorMessages = await db
    .select()
    .from(ticketMessages)
    .where(and(eq(ticketMessages.ticketId, input.ticketId), eq(ticketMessages.role, 'line_manager')))
    .orderBy(ticketMessages.createdAt)

  const priorQAPairs = priorArtifacts.map((artifact, i) => {
    const parsed = artifact.contentJson ? JSON.parse(artifact.contentJson) : {}
    const questions: string[] = parsed.questions ?? []
    const answer = priorMessages[i + 1]?.bodySanitized ?? ''
    return { questions, answer }
  })

  // Enforce max clarification cycles — bypass NEEDS_INFO if exceeded
  const maxCycles = config.maxClarificationCycles
  if (ticket.clarificationCycleCount >= maxCycles && maxCycles > 0) {
    const ticketVersion = await applyTransition(
      db,
      ticket,
      'AWAITING_EXPERT',
      null,
      null,
      input,
      'max_clarification_cycles_reached',
    )
    await sendAssessmentEmail(db, ticket, input.tenantId, 'AWAITING_EXPERT', [])
    return { outcome: 'AWAITING_EXPERT', ticketVersion }
  }

  const { system, user } = buildCompletenessPrompt({
    submissionPromptText: config.submissionPromptText,
    additionalInfoPromptText: config.additionalInfoPromptText,
    submissionText: triggerMessage.bodySanitized,
    priorQAPairs,
    issueCategory: ticket.issueCategory ?? undefined,
    affectedSystem: ticket.affectedSystem ?? undefined,
    actionRequested: ticket.actionRequested ?? undefined,
  })

  let result: CompletenessResult
  try {
    result = await llm(
      {
        provider: config.aiProvider,
        model: config.aiModel,
        temperature: config.aiTemperature,
        dataProcessingConsent: config.dataProcessingConsent,
      },
      system,
      user,
    )
  } catch (err) {
    // LLM failure → fail open to AWAITING_EXPERT with degraded state
    await db
      .update(tickets)
      .set({
        aiProcessingState: 'degraded',
        aiFailureReason: err instanceof Error ? err.message : 'Unknown LLM error',
        status: 'AWAITING_EXPERT',
        version: ticket.version + 1,
        updatedAt: new Date(),
      })
      .where(and(eq(tickets.id, input.ticketId), eq(tickets.version, ticket.version)))

    await writeAuditLog(db, input.tenantId, null, 'ai_agent', 'ticket.ai_degraded', 'ticket', input.ticketId, {
      hook: 'completeness-assessment',
      error: err instanceof Error ? err.message : 'Unknown error',
    })

    await sendAssessmentEmail(db, ticket, input.tenantId, 'AWAITING_EXPERT', [])
    return { outcome: 'AWAITING_EXPERT' }
  }

  const passed = result.score >= config.minInfoScoreThreshold
  const nextStatus = passed ? 'TRIAGED' : 'NEEDS_INFO'

  // Write completeness_assessment artifact
  await db.insert(aiArtifacts).values({
    tenantId: input.tenantId,
    ticketId: input.ticketId,
    sourceMessageId: input.messageId,
    triggerEvent: 'ticket/submitted',
    artifactType: 'completeness_assessment',
    content: `Completeness score: ${result.score.toFixed(2)}. Missing: ${result.missing_dimensions.join(', ') || 'none'}.`,
    contentJson: JSON.stringify(result),
    modelUsed: config.aiModel,
    promptVersion: '1.0.0',
    validationStatus: 'valid',
  })

  // Write clarification_questions artifact if NEEDS_INFO
  if (!passed && result.questions.length > 0) {
    await db.insert(aiArtifacts).values({
      tenantId: input.tenantId,
      ticketId: input.ticketId,
      sourceMessageId: input.messageId,
      triggerEvent: 'ticket/submitted',
      artifactType: 'clarification_questions',
      content: result.questions.map((q, i) => `${i + 1}. ${q}`).join('\n'),
      contentJson: JSON.stringify({ questions: result.questions }),
      modelUsed: config.aiModel,
      promptVersion: '1.0.0',
      validationStatus: 'valid',
    })
  }

  const ticketVersion = await applyTransition(
    db,
    ticket,
    nextStatus,
    result.score,
    result,
    input,
    'completeness_assessment',
  )

  await sendAssessmentEmail(db, ticket, input.tenantId, nextStatus, result.questions)

  return { outcome: nextStatus, score: result.score, ticketVersion }
}

async function sendAssessmentEmail(
  db: DB,
  ticket: typeof tickets.$inferSelect,
  tenantId: string,
  outcome: 'NEEDS_INFO' | 'TRIAGED' | 'AWAITING_EXPERT',
  questions: string[],
) {
  if (!process.env.EMAIL_SERVER_HOST) return
  try {
    const [submitter] = await db
      .select({ email: users.email })
      .from(users)
      .where(eq(users.id, ticket.submitterId))
      .limit(1)

    if (!submitter?.email) return

    const transport = createTransport({
      host: process.env.EMAIL_SERVER_HOST,
      port: Number(process.env.EMAIL_SERVER_PORT ?? 587),
      auth: process.env.EMAIL_SERVER_USER
        ? { user: process.env.EMAIL_SERVER_USER, pass: process.env.EMAIL_SERVER_PASSWORD }
        : undefined,
    })

    const ref = ticket.referenceNumber ?? ticket.id
    const title = ticket.title ?? 'Your ticket'

    let email: { subject: string; html: string; text: string }
    if (outcome === 'NEEDS_INFO' && questions.length > 0) {
      email = buildClarificationQuestionsEmail({ ticketReference: ref, ticketTitle: title, questions, ticketId: ticket.id })
    } else {
      email = buildTicketQueuedEmail({ ticketReference: ref, ticketTitle: title, ticketId: ticket.id })
    }

    await transport.sendMail({ to: submitter.email, from: process.env.EMAIL_FROM, ...email })
  } catch (err) {
    console.error('[completeness-agent] Failed to send assessment email:', err)
  }
}

async function applyTransition(
  db: DB,
  ticket: typeof tickets.$inferSelect,
  nextStatus: 'NEEDS_INFO' | 'TRIAGED' | 'AWAITING_EXPERT',
  score: number | null,
  result: CompletenessResult | null,
  input: AssessCompletenessInput,
  reason: string,
) {
  transition(ticket.status as 'SUBMITTED', nextStatus)

  const cycleIncrement = nextStatus === 'NEEDS_INFO' ? 1 : 0

  const [updatedTicket] = await db
    .update(tickets)
    .set({
      status: nextStatus,
      aiProcessingState: 'idle',
      infoCompletenessScore: score ?? undefined,
      clarificationCycleCount: ticket.clarificationCycleCount + cycleIncrement,
      version: ticket.version + 1,
      updatedAt: new Date(),
    })
    .where(and(eq(tickets.id, input.ticketId), eq(tickets.version, ticket.version)))
    .returning({ version: tickets.version })

  await writeAuditLog(db, input.tenantId, null, 'ai_agent', 'ticket.status_changed', 'ticket', input.ticketId, {
    from: ticket.status,
    to: nextStatus,
    reason,
    score,
  })

  return updatedTicket?.version ?? ticket.version + 1
}

async function writeAuditLog(
  db: DB,
  tenantId: string,
  actorId: string | null,
  actorType: 'user' | 'system' | 'ai_agent',
  action: string,
  entityType: string,
  entityId: string,
  payload: Record<string, unknown>,
) {
  await db.insert(auditLog).values({
    tenantId,
    actorId,
    actorType,
    action,
    entityType,
    entityId,
    payload,
  })
}

// Inngest function wrapper (v4 API: triggers go in the first argument)
export const completenessAssessmentFn = inngest.createFunction(
  { id: 'completeness-assessment', retries: 3, triggers: [{ event: 'ticket/submitted' }] },
  async ({ event, step }) => {
    const input = event.data as AssessCompletenessInput
    const result = await step.run('run-assessment', () => runCompletenessAssessment(input))

    if (result.outcome === 'TRIAGED' && result.ticketVersion) {
      await step.sendEvent('queue-summarization', {
        name: 'ticket/triaged',
        data: {
          ...input,
          ticketVersion: result.ticketVersion,
        },
      })
    }

    return result
  },
)
